
import React from "react";
import * as ReactRouterDom from "react-router-dom";
import { Classname } from "@rivtn/noseur";
import Btn from "../common/Btn";
import TeamCard from "../common/TeamCard";
import { i18nManager } from "../../i18n";
import SectionHeader from "../common/SectionHeader";
import styles from "../../assets/style.module.css";
import { useInView, useItemsPerView } from "../../hooks";
import { pagePath } from "../../helpers";
import { IProfileWebsite, ITeamHistory } from "../../../../../services";

export interface TeamsCarouselProps {
    basePath: string;
    website: IProfileWebsite;
    teamHistories: ITeamHistory[];
}

function TeamsCarousel({ website, basePath, teamHistories }: TeamsCarouselProps) {

    const [page, setPage] = React.useState(0);
    const [ref, inView] = useInView();
    const perView = useItemsPerView();
    const labels = i18nManager.Labels.home;
    const [searchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const total = Math.max(1, Math.ceil(teamHistories.length / perView));
    const stats = website.extra_data?.stats ? Object.entries(website.extra_data.stats).slice(0, 4) : [];

    React.useEffect(() => {
        if (page >= total) setPage(total - 1);
    }, [page, total]);

    React.useEffect(() => {
        if (!inView || total < 2) return;
        const t = setInterval(() => setPage((p) => (p + 1) % total), 5000);
        return () => clearInterval(t);
    }, [inView, total]);

    if (!teamHistories.length) return null;

    return (<section className={styles["section"]}>
        <div className={styles["container"]}>
            <SectionHeader eyebrow={labels.teams_eyebrow} title={labels.teams_title} titleEm={labels.teams_title_em}
                action={<Btn variant="ghost" size="sm" to={{ pathname: pagePath(basePath, "TEAM_HISTORY"), search: searchParams.toString() }}>{i18nManager.Labels.view_all} <i className="fa fa-arrow-right" /></Btn>} />

            <div ref={ref} className={styles["carousel"]}>
                <div className={styles["carousel-track"]} style={{ transform: `translateX(calc(-${page} * (100% + 20px)))` }}>
                    {teamHistories.map((t) => (<TeamCard key={t.external_id} team={t} visible={inView} />))}
                </div>
            </div>

            {total > 1 && (<div className={styles["carousel-ctrl"]}>
                <button className={styles["carousel-arrow"]} disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹</button>
                {Array.from({ length: total }, (_, i) => (
                    <button key={i} className={Classname.build(styles["carousel-dot"], { [styles["active"]]: i === page })} onClick={() => setPage(i)} />
                ))}
                <button className={styles["carousel-arrow"]} disabled={page === total - 1} onClick={() => setPage((p) => Math.min(total - 1, p + 1))}>›</button>
            </div>)}

            {(website.home_settings?.show_stats && stats.length > 0) && (<div className={styles["totals"]}>
                {stats.map(([k, v]) => (<div key={k} className={styles["total"]}>
                    <div className={styles["total-num"]}>{v as React.ReactNode}</div>
                    <div className={styles["total-lbl"]}>{k.replaceAll("_", " ")}</div>
                </div>))}
            </div>)}
        </div>
    </section>);
}

export default TeamsCarousel;
